import { api } from 'api';
import { UploadFile } from 'types';

import { EmptyPlaceholder } from '../config';

import { getFileByBlobUrl } from './rich-text-storage';

export const formatBytes = (
  bytes: number,
  decimals: number = 2,
  placeholder: string = EmptyPlaceholder
) => {
  if (!bytes) {
    return placeholder;
  }

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + sizes[i];
};

export const isUploadFile = (value: unknown): value is UploadFile => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'src' in value &&
    typeof (value as any).src === 'string' &&
    'file' in value &&
    (value as any).file instanceof File
  );
};

export const downloadBlob = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = fileName;
  link.click();

  window.URL.revokeObjectURL(url);
};

export const handleUploadableValue = async (
  value: any
): Promise<string | null> => {
  if (!value) {
    return null;
  }
  if (typeof value === 'string') {
    return value;
  }
  if (isUploadFile(value)) {
    const formData = new FormData();
    formData.append('image', value.file);
    const url = await api.upload.image(formData);
    return url;
  }
  return value;
};

export const processFormWithUploadsAndRichText = async <
  T extends Record<string, any>,
>(
  data: T,
  uploadFields: (keyof T)[],
  richTextFields: (keyof T)[] = []
): Promise<T> => {
  const processedData = { ...data };

  // Process regular file upload fields
  for (const field of uploadFields) {
    if (data[field]) {
      processedData[field] = (await handleUploadableValue(
        data[field]
      )) as T[keyof T];
    }
  }

  // Process rich text fields with embedded images
  for (const field of richTextFields) {
    if (data[field] && typeof data[field] === 'string') {
      processedData[field] = (await extractAndUploadImages(
        field as string,
        data[field]
      )) as T[keyof T];
    }
  }

  return processedData;
};

export const extractAndUploadImages = async (
  fieldName: string,
  html: string | null | undefined
): Promise<string> => {
  if (!html) {
    return '';
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const images = doc.querySelectorAll('img[src^="blob:"]');

  for (const img of images) {
    const blobUrl = img.getAttribute('src');
    if (!blobUrl) {
      continue;
    }

    const file = getFileByBlobUrl(fieldName, blobUrl);
    if (!file) {
      continue;
    }

    const formData = new FormData();
    formData.append('image', file);
    const uploadedUrl = await api.upload.image(formData);
    img.setAttribute('src', uploadedUrl);
  }

  return doc.body.innerHTML;
};
